import React from 'react';
import { CrossIcon } from '../../icons';
import { AlertNoticeColor } from '../../theming';
import { IconButton } from '../IconButton';

import { Styler, StylerProps } from '../Styler/Styler';
import { SvgIconProps } from '../SvgIcon';
import { STATIC_STYLES, useDynamicStyles } from './AlertNotice.styles';

export interface AlertNoticeProps extends StylerProps {
    /**
     * The contents of the notice.
     */
    children?: React.ReactNode;

    /**
     * The colour variant of the button.
     * @default primary
     */
    color?: AlertNoticeColor;

    /**
     * An svg icon component. The icon will be displayed before the content.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * Callback fired when the component requests to be dismissed.
     * If provided, the component will be have a `dismiss` button.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onDismiss?: (event: React.SyntheticEvent) => void;
}

export const AlertNotice: React.ForwardRefExoticComponent<AlertNoticeProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function AlertNotice(
        props: AlertNoticeProps,
        ref: React.ForwardedRef<HTMLElement>
    ): React.JSX.Element {
        const { children, color = 'primary', icon, onDismiss, style, ...other } = props;

        const cssVariables = useDynamicStyles(color);

        const dismissible = typeof onDismiss === 'function';

        const handleDismissClick = (event: React.SyntheticEvent) => {
            event.stopPropagation();
            onDismiss?.(event);
        };

        return (
            <Styler
                ref={ref}
                style={{ ...cssVariables, ...((style as object) || {}) }}
                baseStyles={STATIC_STYLES}
                data-alert-notice={true}
                role="alert"
                {...other}
            >
                {icon && (
                    <div data-graphic={true}>
                        {React.cloneElement(icon, {
                            'data-icon': 'true',
                            ...icon.props,
                        })}
                    </div>
                )}
                <div data-content={true}>{children}</div>
                {dismissible && (
                    <IconButton size="xs" onClick={handleDismissClick} alignEdge="end" data-close-button={true}>
                        <CrossIcon data-close={true} onClick={handleDismissClick} />
                    </IconButton>
                )}
            </Styler>
        );
    });
